// QuestionSingleSelectCheckboxDropdown.js
import React, { useState } from "react";
import {
  QuestionDropdownModel,
  QuestionFactory,
  Serializer,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "singleSelectCheckbox";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL
========================================================= */
class QuestionSingleSelectCheckboxModel extends QuestionDropdownModel {
  constructor(name) {
    super(name);
    this.createNewArray("choices");
  }

  getType() {
    return QUESTION_TYPE;
  }

  get placeholder() {
    return this.getPropertyValue("placeholder", "Select an option");
  }

  set placeholder(val) {
    this.setPropertyValue("placeholder", val);
  }
}

/* =========================================================
   REACT RENDERER - Dropdown with checkboxes
========================================================= */
class SurveyQuestionSingleSelectCheckbox extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
    this.dropdownRef = React.createRef();
  }

  get question() {
    return this.props.question;
  }

  componentDidMount() {
    // Close dropdown when clicking outside
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)
    ) {
      this.setState({ isOpen: false });
    }
  };

  toggleDropdown = () => {
    if (!this.question.isReadOnly) {
      this.setState((prev) => ({ isOpen: !prev.isOpen }));
    }
  };

  handleSelect = (choiceValue) => {
    // Toggle selection: if already selected, deselect; otherwise select
    if (this.question.value === choiceValue) {
      this.question.value = null;
    } else {
      this.question.value = choiceValue;
    }
    // Close dropdown after selection
    this.setState({ isOpen: false });
  };

  renderElement() {
    const q = this.question;
    const selectedValue = q.value;
    const choices = q.visibleChoices || q.choices || [];
    const { isOpen } = this.state;

    // Get selected text for display
    const selectedChoice = choices.find((c) => c.value === selectedValue);
    const displayText = selectedChoice ? selectedChoice.text : q.placeholder;

    return (
      <div
        ref={this.dropdownRef}
        style={{ width: "100%", position: "relative" }}
      >
        {/* Dropdown trigger */}
        <div
          onClick={this.toggleDropdown}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 40px 8px 12px",
            borderRadius: "4px",
            border: `1px solid ${q.errors.length > 0 ? "#dc3545" : "#ced4da"}`,
            backgroundColor: q.isReadOnly ? "#e9ecef" : "#fff",
            fontSize: "16px",
            cursor: q.isReadOnly ? "not-allowed" : "pointer",
            color: selectedValue ? "#212529" : "#6c757d",
            position: "relative",
            minHeight: "38px",
            display: "flex",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          <span style={{ flex: 1 }}>{displayText}</span>

          {/* Dropdown arrow */}
          <div
            style={{
              position: "absolute",
              right: "12px",
              top: "20%",
              transform: "translateY(-50%)",
              color: "#6c757d",
              transition: "transform 0.2s",
              //   paddingTop: "-20px",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </div>
        </div>

        {/* Dropdown options panel */}
        {isOpen && !q.isReadOnly && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 1000,
              marginTop: "4px",
              maxHeight: "250px",
              overflowY: "auto",
              animation: "fadeIn 0.2s ease",
            }}
          >
            {choices.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#6c757d",
                  fontSize: "14px",
                  fontStyle: "italic",
                }}
              >
                No options available. Add choices in settings.
              </div>
            ) : (
              choices.map((choice, index) => {
                const isSelected = selectedValue === choice.value;
                const choiceId = `${q.id}_option_${index}`;

                return (
                  <div
                    key={choice.value || index}
                    onClick={() => this.handleSelect(choice.value)}
                    style={{
                      padding: "10px 12px",
                      borderBottom: "1px solid #f8f9fa",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#e7f3ff" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = isSelected
                        ? "#e7f3ff"
                        : "transparent")
                    }
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        border: `2px solid ${
                          isSelected ? "#007bff" : "#adb5bd"
                        }`,
                        borderRadius: "3px",
                        backgroundColor: isSelected ? "#007bff" : "white",
                        position: "relative",
                        flexShrink: 0,
                        transition: "all 0.2s",
                      }}
                    >
                      {/* Checkmark */}
                      {isSelected && (
                        <div
                          style={{
                            position: "absolute",
                            top: "1px",
                            left: "4px",
                            width: "4px",
                            height: "8px",
                            border: "solid white",
                            borderWidth: "0 2px 2px 0",
                            transform: "rotate(45deg)",
                          }}
                        />
                      )}
                    </div>

                    {/* Option text */}
                    <label
                      htmlFor={choiceId}
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "#212529",
                        userSelect: "none",
                        flex: 1,
                      }}
                    >
                      {choice.text || choice.value}
                    </label>

                    {/* Selected indicator */}
                    {isSelected && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#007bff",
                          fontWeight: "600",
                        }}
                      >
                        Selected
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Clear selection option */}
            {selectedValue && (
              <div
                onClick={() => {
                  this.question.value = null;
                  this.setState({ isOpen: false });
                }}
                style={{
                  padding: "10px 12px",
                  borderTop: "1px solid #dee2e6",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#dc3545",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #dc3545",
                    borderRadius: "3px",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "2px",
                      width: "8px",
                      height: "2px",
                      backgroundColor: "#dc3545",
                    }}
                  />
                </div>
                Clear selection
              </div>
            )}
          </div>
        )}

        {/* Add CSS animation */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }
}

/* =========================================================
   REGISTRATION
========================================================= */
export function registerSingleSelectCheckboxQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "placeholder:text",
        category: "general",
        default: "Select an option",
        visible: true,
      },
    ],
    () => new QuestionSingleSelectCheckboxModel(""),
    "dropdown"
  );

  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionSingleSelectCheckboxModel(name)
  );

  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionSingleSelectCheckbox, props)
  );
}

registerSingleSelectCheckboxQuestion();

export default QuestionSingleSelectCheckboxModel;
